import { useCallback, useState } from 'react';
import { useTranslation } from '../lib/i18n';
import { API_BASE } from '../lib/api-base';
import { requestGoogleCalendarAuthorization } from '../lib/google-calendar-code';
import { useGenericBi } from '../lib/use-generic-bi';
import { Input } from './symphony/ui';

// Shared calendar-connect plumbing used by both the onboarding step and the
// settings Calendars section: the provider catalog + glyphs, the iCloud
// credentials form, and the connect hook (OAuth popup / Nylas custom auth).

// OAuth popup lands on the connect/success page — that page postMessages back to
// the opener with {type: 'authSuccess'}. The same page is served on both hosts
// (chat.nownia.com and its symphony.wix.com successor), so accept either origin:
// the server-side redirect target can move between them without breaking connect.
const POPUP_ORIGINS = new Set(['https://chat.nownia.com', 'https://symphony.wix.com']);

/** Where a direct Google connect ended. `refused` is a settled answer — the user closed the consent
 *  window, or the account is not provisioned — while `unavailable` means the path could not be used at
 *  all and the hosted one is still worth trying. */
type GoogleDirectOutcome = 'connected' | 'refused' | 'unavailable';

export type ProviderId = 'google' | 'microsoft' | 'apple';

export type Provider = {
  id: ProviderId;
  labelKey: string;
  glyph: React.ReactNode;
} & (
  | {
      // Casing the serverless individual-calendar-oauth-url endpoint expects.
      kind: 'oauth';
      serverProvider: 'GOOGLE' | 'MICROSOFT';
    }
  // iCloud connects with an Apple ID + app-specific password (Nylas custom
  // auth), not OAuth — so its row expands an inline form instead of a popup.
  | { kind: 'icloud' }
);

export const PROVIDERS: Provider[] = [
  { id: 'google', labelKey: 'calendar.provider.google', kind: 'oauth', serverProvider: 'GOOGLE', glyph: <GoogleGlyph /> },
  { id: 'apple', labelKey: 'calendar.provider.apple', kind: 'icloud', glyph: <AppleGlyph /> },
  { id: 'microsoft', labelKey: 'calendar.provider.microsoft', kind: 'oauth', serverProvider: 'MICROSOFT', glyph: <OutlookGlyph /> },
];

// Maps the CalendarToken.provider (GOOGLE | MICROSOFT | ICLOUD) to a catalog row,
// so a connected token can borrow the catalog's label + glyph.
export function providerForToken(tokenProvider: string): Provider | undefined {
  const id: ProviderId | null =
    tokenProvider === 'GOOGLE' ? 'google' : tokenProvider === 'MICROSOFT' ? 'microsoft' : tokenProvider === 'ICLOUD' ? 'apple' : null;
  return id ? PROVIDERS.find((p) => p.id === id) : undefined;
}

const APPLE_APP_PASSWORDS_URL = 'https://account.apple.com/account/manage';
const APPLE_PASSWORD_STEP_KEYS = [
  'calendar.icloud.step1',
  'calendar.icloud.step2',
  'calendar.icloud.step3',
  'calendar.icloud.step4',
];

export type CalendarConnectErrorKind = 'not_set_up' | 'generic';

interface CalendarConnect {
  /** Provider whose connect is in flight, or null. */
  pending: ProviderId | null;
  error: string | null;
  /** What failed — 'not_set_up' means the account has no Orion presence yet
   *  (retrying the connect can never help; the user must finish the phone
   *  step), anything else is worth a plain retry. */
  errorKind: CalendarConnectErrorKind | null;
  setError: (message: string | null) => void;
  /** Opens the OAuth popup and resolves true once the provider reports success. */
  connectOAuth: (provider: Provider) => Promise<boolean>;
  /** Nylas custom auth with an Apple ID + app-specific password. */
  connectICloud: (appleId: string, appSpecificPassword: string) => Promise<boolean>;
}

export function useCalendarConnect(): CalendarConnect {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [pending, setPending] = useState<ProviderId | null>(null);
  const [error, setErrorMessage] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<CalendarConnectErrorKind | null>(null);
  // Stable identity — consumers may legitimately list this in hook deps.
  const setError = useCallback((message: string | null, kind: CalendarConnectErrorKind | null = null) => {
    setErrorMessage(message);
    setErrorKind(message === null ? null : kind ?? 'generic');
  }, []);

  async function connectICloud(appleId: string, appSpecificPassword: string): Promise<boolean> {
    if (pending || !appleId.trim() || !appSpecificPassword.trim()) return false;
    setPending('apple');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/calendar-connect-icloud`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appleId: appleId.trim(), appSpecificPassword: appSpecificPassword.trim() }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return true;
    } catch (err) {
      console.warn('[calendar-connect-icloud]', err);
      setError(t('calendar.connect.error.icloud'));
      return false;
    } finally {
      setPending(null);
    }
  }

  /**
   * Google's own consent popup, then the code straight to our server.
   *
   * No `postMessage` handshake and no popup bookkeeping: Google Identity Services owns the window and
   * resolves with the code, so the two failure modes the hosted flow has to infer from a closed
   * window — dismissed and blocked — arrive named.
   *
   * Reports where the attempt ended, because only some endings reach the server: everything from the
   * code onward is in its logs, while a blocked script or a dismissed consent is visible nowhere else.
   */
  async function attemptGoogleDirect(): Promise<GoogleDirectOutcome> {
    const report = (outcome: string) => logPageClickBi({ page_name: 'calendar_connect', button_name: 'google_direct_connect', action: outcome });
    try {
      const browserAuthCode = await requestGoogleCalendarAuthorization();
      const res = await fetch(`${API_BASE}/api/calendar-connect-google`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ browserAuthCode }),
      });
      // The bridge answers 409 for an account with no Orion presence yet — a state no retry can clear,
      // because the user has to finish the phone step first, so the hosted flow would fail identically.
      if (res.status === 409) {
        report('recipient_not_found');
        setError(t('calendar.connect.error.notSetUp'), 'not_set_up');
        return 'refused';
      }
      if (!res.ok) throw new Error(`${res.status}`);
      report('connected');
      return 'connected';
    } catch (err) {
      console.warn('[calendar-connect-google]', err);
      const message = err instanceof Error ? err.message : '';
      report(message || 'unknown_error');
      // Dismissing the consent window is an answer, not a fault: offering a second popup would
      // reopen the thing the user just closed.
      return message === 'popup_closed' ? 'refused' : 'unavailable';
    }
  }

  async function connectOAuth(provider: Provider): Promise<boolean> {
    if (provider.kind !== 'oauth' || pending) return false;
    // Google connects through Google. Should that path be unavailable — its script blocked, its popup
    // refused, our exchange failing — the hosted flow still connects the calendar, so the user gets a
    // working connection rather than an error. The credential is the poorer of the two, and the BI
    // above is what says how often that happens.
    if (provider.id === 'google') {
      setPending('google');
      setError(null);
      const outcome = await attemptGoogleDirect();
      if (outcome !== 'unavailable') {
        setPending(null);
        return outcome === 'connected';
      }
      // Falling through with `pending` still set: clearing it here would blink the spinner off between
      // the two attempts, which reads as a failed click.
    }
    setPending(provider.id);
    setError(null);
    let popup: Window | null = null;
    try {
      const res = await fetch(`${API_BASE}/api/calendar-connect?provider=${provider.serverProvider}`, { credentials: 'include' });
      if (!res.ok) {
        // The body's error code separates "this account isn't set up" (a 409
        // retrying can never fix) from transient failures (worth a retry).
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error || `${res.status}`);
      }
      const { oauthUrl } = (await res.json()) as { oauthUrl?: string };
      if (!oauthUrl) throw new Error('missing_oauth_url');

      popup = window.open(oauthUrl, 'orion-calendar-oauth', 'width=520,height=720');
      if (!popup) throw new Error('popup_blocked');

      // Race the success postMessage against an explicit failure signal (the connect/failure
      // page — e.g. the Outlook token exchange rejecting) or the user closing the popup.
      await new Promise<void>((resolve, reject) => {
        function onMessage(event: MessageEvent) {
          if (!POPUP_ORIGINS.has(event.origin)) return;
          if (event.data?.type === 'authFailure') {
            cleanup();
            reject(new Error('auth_failed'));
            return;
          }
          if (event.data?.type !== 'authSuccess') return;
          cleanup();
          resolve();
        }
        const pollClosed = setInterval(() => {
          if (popup?.closed) {
            cleanup();
            reject(new Error('popup_closed'));
          }
        }, 400);
        function cleanup() {
          window.removeEventListener('message', onMessage);
          clearInterval(pollClosed);
        }
        window.addEventListener('message', onMessage);
      });
      return true;
    } catch (err) {
      console.warn('[calendar-connect]', err);
      // Popup-closed is user-driven (they hit X) — silent. Anything else
      // (popup blocked, fetch failed, OAuth URL fetch errored) deserves visible
      // feedback so the user knows their click had an effect.
      const message = err instanceof Error ? err.message : '';
      if (message === 'popup_blocked') {
        setError(t('calendar.connect.error.popupBlocked'));
      } else if (message === 'no_orion_metasite') {
        setError(t('calendar.connect.error.notSetUp'), 'not_set_up');
      } else if (message !== 'popup_closed') {
        setError(t('calendar.connect.error.generic'));
      }
      return false;
    } finally {
      popup?.close();
      setPending(null);
    }
  }

  return { pending, error, errorKind, setError, connectOAuth, connectICloud };
}

export function GoogleGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.8 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.3 0 6.3 1.2 8.6 3.2l6.2-6.2C34.6 4.4 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" />
      <path fill="#34A853" d="M6.3 14.7l7 5.1C15.1 16.1 19.2 13 24 13c3.3 0 6.3 1.2 8.6 3.2l6.2-6.2C34.6 6.4 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
      <path fill="#FBBC05" d="M24 46c5.5 0 10.5-1.9 14.4-5.2l-6.6-5.6C29.4 36.7 26.9 37.7 24 37.7c-6 0-10.7-3.2-12.6-7.6l-7 5.4C8 41.7 15.3 46 24 46z" />
      <path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-1.2 3.2-3.6 5.6-6.6 6.6l6.6 5.6C40.7 36.6 45 30.9 45 24c0-1.3-.2-2.7-.5-4z" />
    </svg>
  );
}

export function OutlookGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden>
      <rect x="3" y="9" width="42" height="30" rx="3" fill="#0078D4" />
      <rect x="3" y="15" width="22" height="20" fill="#106EBE" />
      <ellipse cx="14" cy="25" rx="5.5" ry="6" fill="#FFFFFF" />
      <ellipse cx="14" cy="25" rx="2.5" ry="3" fill="#0078D4" />
      <path d="M26 18l9 6 9-6v15h-18z" fill="#28A8EA" />
    </svg>
  );
}

// Apple logo tinted with --ink-rgb so it reads on both the dark Orion field
// and the light Symphony one, where a flat-black mark would disappear.
export function AppleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="rgba(var(--ink-rgb),0.9)">
      <path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-1.71-.92-2.82-.9-1.45.02-2.79.84-3.54 2.14-1.51 2.62-.39 6.5 1.08 8.63.72 1.04 1.57 2.21 2.69 2.17 1.08-.04 1.49-.7 2.79-.7 1.3 0 1.67.7 2.81.68 1.16-.02 1.9-1.06 2.61-2.11.82-1.2 1.16-2.37 1.18-2.43-.03-.01-2.26-.87-2.29-3.44zM14.47 4.62c.6-.73 1-1.74.89-2.75-.86.03-1.9.57-2.52 1.3-.55.64-1.04 1.67-.91 2.65.96.08 1.94-.49 2.54-1.2z" />
    </svg>
  );
}

export function ICloudForm({
  appleId,
  applePassword,
  submitting,
  onAppleId,
  onApplePassword,
  onSubmit,
}: {
  appleId: string;
  applePassword: string;
  submitting: boolean;
  onAppleId: (v: string) => void;
  onApplePassword: (v: string) => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const canSubmit = appleId.trim().length > 0 && applePassword.trim().length > 0 && !submitting;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px',
        marginTop: -2,
        borderRadius: 12,
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
      }}
    >
      <div style={{ fontSize: 13.5, color: 'rgba(var(--ink-rgb),0.6)', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 'var(--sym-weight-semibold)', color: 'rgba(var(--ink-rgb),0.8)', marginBottom: 6 }}>{t('calendar.icloud.heading')}</div>
        <ol style={{ margin: 0, paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {APPLE_PASSWORD_STEP_KEYS.map((stepKey) => (
            <li key={stepKey}>{t(stepKey)}</li>
          ))}
        </ol>
        <a
          href={APPLE_APP_PASSWORDS_URL}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-block',
            marginTop: 8,
            color: 'rgba(var(--accent-rgb),0.95)',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(var(--accent-rgb),0.4)',
          }}
        >
          {t('calendar.icloud.openAppleLink')}
        </a>
      </div>
      <Input
        skin="sunken"
        type="email"
        inputMode="email"
        autoComplete="username"
        placeholder={t('calendar.icloud.appleIdPlaceholder')}
        value={appleId}
        onChange={(e) => onAppleId(e.target.value)}
      />
      <Input
        skin="sunken"
        type="password"
        autoComplete="off"
        placeholder="xxxx-xxxx-xxxx-xxxx"
        value={applePassword}
        onChange={(e) => onApplePassword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSubmit) onSubmit();
        }}
      />
      <button type="button" className="orion-pill-button" disabled={!canSubmit} onClick={onSubmit} style={{ alignSelf: 'flex-start' }}>
        {submitting ? t('calendar.icloud.submitting') : t('calendar.icloud.submit')}
      </button>
    </div>
  );
}
